/**
 * The keyframe write rules (spec 006), once, as pure functions over one
 * track's `Keyframe[]`. The scene store's `addKeyframe`, `updateKeyframe`,
 * `addKeyframeAtPlayhead` and `getKeyframeValueAt` wrap them with its own
 * `kf_` ids; entrance and exit presets (016) and the assistant's keyframe
 * operations (036) call them. Nothing else declares a slot, fork or value
 * rule.
 *
 * - The slot invariant: within one track no two keyframes share time +
 *   property + orientation; a write onto an occupied slot replaces it.
 * - The orientation fork: a tagged write in portrait or landscape on a
 *   property animated there only by untagged keyframes first copies those
 *   as that orientation's keyframes (new ids).
 * - The value rule: a new keyframe takes the property's current value, the
 *   authored keyframes' interpolated value when animated, else the base value.
 */

import type { Keyframe, KeyframeEasing, Orientation, Sprite } from '../@types';
import { interpolateProperty } from './interpolate';

/** `transform.flipX` or `transform.flipY` (spec 038). */
export const isFlipProperty = (property: string): property is 'transform.flipX' | 'transform.flipY' =>
  property === 'transform.flipX' || property === 'transform.flipY';
import { getResponsivePosition, getResponsiveTransform } from './responsiveLayout';

export type { KeyframeEasing };

/** The four easings, in the order of interpolation rule 3. */
export const KEYFRAME_EASINGS: readonly KeyframeEasing[] = Object.freeze(['linear', 'easeIn', 'easeOut', 'easeInOut'] as KeyframeEasing[]);

/** A source of new keyframe ids (the store's `kf_…`, a test's counter). */
export type NewKeyframeId = () => string;

const isForkOrientation = (o: Orientation | undefined): o is 'portrait' | 'landscape' =>
  o === 'portrait' || o === 'landscape';

/** Untagged or desktop-tagged: the keyframes desktop reads, and portrait and landscape fall back to. */
export const isUntaggedKeyframe = (kf: Keyframe): boolean => !kf.orientation || kf.orientation === 'desktop';

const sameSlot = (a: Pick<Keyframe, 'time' | 'property' | 'orientation'>, b: Pick<Keyframe, 'time' | 'property' | 'orientation'>) =>
  a.time === b.time && a.property === b.property && (a.orientation ?? undefined) === (b.orientation ?? undefined);

/**
 * Put `kf` on a track: any keyframe with its id, and any occupant of its
 * time + property + orientation slot, is replaced. Returns a new array.
 */
export function putKeyframe(keyframes: readonly Keyframe[], kf: Keyframe): Keyframe[] {
  return [...keyframes.filter((k) => (k.id === kf.id ? false : !sameSlot(k, kf))), kf];
}

/**
 * The orientation fork: when `orientation` is portrait or landscape, the
 * track has no keyframe of `property` tagged with it and does have untagged
 * ones, those are copied as `orientation`-tagged keyframes with ids from
 * `newId`. Otherwise a no-op (the same array). `copies` maps each copied
 * keyframe's id to its copy's id, so an edit of an untagged keyframe can
 * apply to its copy.
 */
export function forkOrientation(
  keyframes: readonly Keyframe[],
  property: string,
  orientation: Orientation | undefined,
  newId: NewKeyframeId,
): { keyframes: Keyframe[]; copies: Map<string, string> } {
  const copies = new Map<string, string>();
  const same = keyframes as Keyframe[];
  if (!isForkOrientation(orientation)) return { keyframes: same, copies };
  if (keyframes.some((k) => k.property === property && k.orientation === orientation)) return { keyframes: same, copies };
  const untagged = keyframes.filter((k) => k.property === property && isUntaggedKeyframe(k));
  if (untagged.length === 0) return { keyframes: same, copies };
  let next: Keyframe[] = [...keyframes];
  for (const k of untagged) {
    const copy: Keyframe = { ...k, id: newId(), orientation };
    copies.set(k.id, copy.id);
    next = putKeyframe(next, copy);
  }
  return { keyframes: next, copies };
}

/** Write one keyframe: the fork for its property and orientation, then `putKeyframe`. */
export function writeKeyframe(keyframes: readonly Keyframe[], kf: Keyframe, newId: NewKeyframeId): Keyframe[] {
  return putKeyframe(forkOrientation(keyframes, kf.property, kf.orientation, newId).keyframes, kf);
}

/** A property's base (un-animated) value for a sprite in an orientation. */
export function keyframeBaseValue(
  sprite: Pick<Sprite, 'layout' | 'transform' | 'hinge' | 'defaultState' | 'states'>,
  property: string,
  orientation: Orientation,
): number | string {
  switch (property) {
    case 'position.x': return getResponsivePosition(sprite.layout, orientation).x;
    case 'position.y': return getResponsivePosition(sprite.layout, orientation).y;
    case 'hinge.x': return (sprite.hinge?.x ?? 0.5) * 100;
    case 'hinge.y': return (sprite.hinge?.y ?? 0.5) * 100;
    case 'transform.scale': return getResponsiveTransform(sprite.layout, orientation, sprite.transform).scale;
    case 'transform.rotation': return getResponsiveTransform(sprite.layout, orientation, sprite.transform).rotation || 0;
    // The resolved at-rest flip as 1 or 0 (spec 038 D-3).
    case 'transform.flipX': return getResponsiveTransform(sprite.layout, orientation, sprite.transform).flipX ? 1 : 0;
    case 'transform.flipY': return getResponsiveTransform(sprite.layout, orientation, sprite.transform).flipY ? 1 : 0;
    case 'opacity': return 1;
    case 'state': return sprite.defaultState || (sprite.states?.[0]?.name ?? '');
    default: return 0;
  }
}

/**
 * The value a keyframe written now for `property` at `timeMs` in
 * `orientation` takes: the authored keyframes' interpolated value there when
 * the property is animated, otherwise the base value from the resolved
 * layout (orientation overrides honoured), 1 for opacity, or the default
 * state for `state`. A flip is always `1` or `0` (spec 038).
 */
export function keyframeValueAt(
  sprite: Pick<Sprite, 'layout' | 'transform' | 'hinge' | 'defaultState' | 'states'>,
  keyframes: readonly Keyframe[] | undefined,
  property: string,
  timeMs: number,
  orientation: Orientation,
): number | string {
  const animated = interpolateProperty(keyframes, property, timeMs, orientation);
  if (isFlipProperty(property) && animated !== undefined) return animated !== 0 && animated !== '0' ? 1 : 0;
  return animated !== undefined ? animated : keyframeBaseValue(sprite, property, orientation);
}
